import type { Tone } from "@/components/workdao/primitives";
import type {
  FactorQuoteStatus,
  FactorSource,
  PrefillState,
} from "@/api/factorQuotes";
import type { ProjectStatus } from "@/api/quoteProjects";
import type { DfmSeverity } from "@/api/dfm";
import {
  TIER_OPTIONS,
  toleranceTierLabel,
  type ToleranceTier,
} from "./toleranceTier";

type FactorSourceMeta = {
  label: string;
  shortLabel: string;
  kind: "ai" | "human" | "reference" | "missing";
};
/** 完整来源与后端SOURCE_ZH逐字一致；短标签不改变来源事实。 */
export const FACTOR_SOURCE_META: Record<FactorSource, FactorSourceMeta> = {
  ai_prior: { label: "AI估算·可修改", shortLabel: "AI", kind: "ai" },
  user_edit: { label: "已修改", shortLabel: "人改", kind: "human" },
  computed: { label: "计算值", shortLabel: "计算", kind: "reference" },
  catalog_default: { label: "目录", shortLabel: "目录", kind: "reference" },
  tenant_default: { label: "租户价", shortLabel: "", kind: "reference" },
  system_default: { label: "系统默认", shortLabel: "", kind: "reference" },
  cad_geometry: { label: "3D几何", shortLabel: "计算", kind: "reference" },
  drawing_llm: {
    label: "图纸识别·请核对",
    shortLabel: "AI · 待核对",
    kind: "ai",
  },
  missing: { label: "待填写", shortLabel: "待填", kind: "missing" },
  project_param: { label: "项目参数", shortLabel: "", kind: "reference" },
  customer_profile: { label: "客户档案", shortLabel: "", kind: "reference" },
  quote_policy: { label: "工厂政策", shortLabel: "", kind: "reference" },
  supplier_offer: { label: "供应商报价", shortLabel: "", kind: "reference" },
  shop_measured: { label: "车间实测", shortLabel: "", kind: "reference" },
  rfq_parsed: {
    label: "需求单解析·请核对",
    shortLabel: "AI · 待核对",
    kind: "ai",
  },
};

export function factorSourceMeta(
  source: unknown,
): FactorSourceMeta | undefined {
  return typeof source === "string" && Object.hasOwn(FACTOR_SOURCE_META, source)
    ? FACTOR_SOURCE_META[source as FactorSource]
    : undefined;
}

export type QuoteStateLabel = { label: string; tone: Tone; detail?: string };
/** 预填（AI 识别）状态码：报价上的四态，多零件行另有「已取消」。 */
export type PrefillStateCode = PrefillState | "cancelled";
/** 报价状态（与后端 status 逐字对齐）。全模块只用这一份（总纲 §5.1 G1）。 */
export const QUOTE_STATES = {
  draft: { label: "草稿", tone: "default" },
  pending_review: { label: "待审批", tone: "warning" },
  approved: { label: "已批准", tone: "success" },
  rejected: { label: "已驳回", tone: "danger" },
} as const satisfies Record<FactorQuoteStatus, QuoteStateLabel>;
/** 预填四态 + 已取消。报价列表不写「已完成」（ready 时只写报价状态），其余地方四种都写。 */
export const PREFILL_STATES = {
  pending: { label: "排队中", tone: "default" },
  running: { label: "AI 识别中", tone: "cyan" },
  ready: { label: "已完成", tone: "default" },
  failed: { label: "识别失败", tone: "danger" },
  cancelled: { label: "已取消", tone: "default" },
} as const satisfies Record<PrefillStateCode, QuoteStateLabel>;
export const PROJECT_STATES = {
  draft: { label: "草稿", tone: "default" },
  pricing: { label: "报价中", tone: "cyan" },
  ready: { label: "已出价", tone: "success" },
  failed: { label: "报价失败", tone: "danger" },
} as const satisfies Record<ProjectStatus, QuoteStateLabel>;
const quoteStates: Record<string, QuoteStateLabel> = QUOTE_STATES;
const prefillStates: Record<string, QuoteStateLabel> = PREFILL_STATES;
const projectStates: Record<string, QuoteStateLabel> = PROJECT_STATES;

/** 批准以后要素又变了（审批绑的输入快照对不上）—— 总纲 §5.1 G1 的说法。 */
export const APPROVAL_STALE_LABEL = "要素已变，需重新审批";
/** 审批记录是更新的版本写的、这一版核对不了（回滚以后会遇到）。 */
export const APPROVAL_UNVERIFIABLE_LABEL =
  "审批记录来自更新的版本 · 这一版核对不了";
/**
 * 「需重新审批」的真实走法：已批准是终态（后端 quote_status 没有从 approved 出去的流转，quote_approval 文档同），
 * 不能在这张单上再批一次，只能新建一张报价走审批（复审 2026-10-25）。
 */
export const APPROVAL_STALE_ROUTE =
  "已批准的报价不能再批一次：要按现在的要素报价，请新建一张报价走审批";
const unknown = (code: string): QuoteStateLabel => ({
  label: "状态待确认",
  tone: "warning",
  detail: `状态代码：${code}`,
});
export const quoteStatusLabel = (status: string) =>
  Object.hasOwn(quoteStates, status) ? quoteStates[status] : unknown(status);

type PrefillFailureKind =
  | "timeout"
  | "interrupted"
  | "geometry"
  | "unknown"
  | "unreported";

/** 只识别已知文字，不在界面重新计算物理关系。 */
function prefillFailureKind(
  error: string | null | undefined,
): PrefillFailureKind {
  if (!error?.trim()) return "unreported";
  if (/\b(?:timeouterror|timeout|timed out)\b|超时/i.test(error))
    return "timeout";
  if (/服务重启|中断|restart|interrupt/i.test(error)) return "interrupted";
  if (
    /FactorInfeasible|(?:尺寸|重量|密度|体积).*(?:冲突|无效|不合理|大于|小于|超过|[<>＜＞])/i.test(
      error,
    )
  )
    return "geometry";
  return "unknown";
}

export function prefillFailureMessage(
  error: string | null | undefined,
): string {
  return {
    unreported: "这次没有返回具体原因，请打开工作表查看并重试。",
    timeout: "识别超时，请打开工作表重试。",
    interrupted: "识别被中断，本轮未完成，请打开工作表重试。",
    geometry: "尺寸、重量或密度有冲突，请先打开工作表核对。",
    unknown: "识别失败，请打开工作表查看并重试。",
  }[prefillFailureKind(error)];
}

export function prefillFailureSummary(
  error: string | null | undefined,
): string {
  return {
    unreported: "识别失败，这次没返回具体原因。",
    timeout: "识别超时，本轮还没完成。",
    interrupted: "识别被中断，本轮没有完成。",
    geometry: "尺寸、重量或密度有冲突。",
    unknown: "识别失败，请查看详情或重试。",
  }[prefillFailureKind(error)];
}
export const projectStatusLabel = (status: string) =>
  Object.hasOwn(projectStates, status)
    ? projectStates[status]
    : unknown(status);
/** 预填状态的叫法，四态都写（ready →「已完成」）；没有状态（旧后端、没草稿）→ null。 */
export function prefillStateLabel(
  state: string | null | undefined,
): QuoteStateLabel | null {
  if (state == null) return null;
  return Object.hasOwn(prefillStates, state)
    ? prefillStates[state]
    : unknown(state);
}
/** 报价列表用：ready 时不另写预填状态（行上已经是报价状态和金额）。 */
export const quotePrefillLabel = (state: string | null | undefined) =>
  state === "ready" ? null : prefillStateLabel(state);

/**
 * 报价摘要、影响面、聊天卡上的那一个状态块：审批失效 > AI 识别中/排队中 > 识别失败 > 报价状态。
 * `staleApproval` 由调用方用 `staleApprovalLabel` 算好传进来（它的文字也取自这里）。
 */
export function quoteDisplayStatusLabel(
  report: {
    status: string;
    display_status?: string | null;
    prefill_state?: string | null;
    /** 阶段三 S3-6:排队位置(只有详情带;旧后端 / 列表不带 → 只写「排队中」) */
    prefill_queue?: { ahead?: number | null } | null;
  },
  staleApproval?: string | null,
): QuoteStateLabel {
  if (staleApproval) return { label: staleApproval, tone: "danger" };
  const prefill = report.prefill_state;
  if (prefill === "pending")
    return queuedLabel(report.prefill_queue?.ahead) ?? PREFILL_STATES.pending;
  if (prefill === "running") return PREFILL_STATES[prefill];
  if (report.display_status === "prefilling") return PREFILL_STATES.running;
  if (report.status === "draft" && prefill === "failed")
    return PREFILL_STATES.failed;
  return quoteStatusLabel(report.status);
}

export const knownQuoteStatus = (status: string) =>
  Object.hasOwn(quoteStates, status);

/* ═══════════════════════════════════════════════════════════════════════
 * 追加段 · 多零件报价行（阶段一 P3 / P7 / F10）
 * 上面已有的条目一字不改；这里只追加多零件行要用的状态词和提醒分级。
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * 多零件行的识别状态词。排队中 / AI 识别中 / 识别失败 / 已取消与报价列表共用上面同一份
 * `prefillStates`；只有 ready 在行上要写出来「已完成」（列表里 ready 不显示状态）。
 * 没草稿或旧后端不给时不显示。
 */
export function projectLinePrefillLabel(
  state: string | null | undefined,
  queueAhead?: number | null,
): QuoteStateLabel | null {
  if (state === "pending")
    return queuedLabel(queueAhead) ?? prefillStateLabel(state);
  return prefillStateLabel(state);
}

/**
 * 排队位置(阶段三 S3-6 · M5.3 · 决定 D3-19):「前面还有 N 张」是**本厂同一条道上**比这张早、还在排队的张数,
 * 所以写「本厂」;N=0 写「下一个就轮到」。不是非负整数(旧后端、说不出名次)→ null,调用方只写「排队中」。
 */
export function queueAheadText(ahead: unknown): string | null {
  if (typeof ahead !== "number" || !Number.isInteger(ahead) || ahead < 0)
    return null;
  return ahead === 0 ? "下一个就轮到" : `本厂前面还有 ${ahead} 张`;
}

function queuedLabel(ahead: unknown): QuoteStateLabel | null {
  const text = queueAheadText(ahead);
  return text
    ? { label: `${PREFILL_STATES.pending.label} · ${text}`, tone: "default" }
    : null;
}

/** 失败原因是不是超时 —— 与上面 `prefillFailureMessage` 用同一个分类，不另写一套判断。 */
export function prefillFailureIsTimeout(
  error: string | null | undefined,
): boolean {
  return prefillFailureKind(error) === "timeout";
}

/** 行提醒三级：挡出价（算不出这一行的价）/ 要核对（价算了但依据要人看）/ 提示（知道就行）。 */
export type ProjectLineBadgeTier = "block" | "review" | "note";

export const PROJECT_LINE_BADGE_TIER_META: Readonly<
  Record<ProjectLineBadgeTier, { label: string; tone: Tone; rank: number }>
> = {
  block: { label: "挡出价", tone: "danger", rank: 0 },
  review: { label: "要核对", tone: "warning", rank: 1 },
  note: { label: "提示", tone: "default", rank: 2 },
};

/** 多零件行上每一种提醒的键（产出见 quoteProjectView `lineBadgeItems`）。 */
export type ProjectLineBadgeKey =
  | "missing_inputs"
  | "no_draft"
  | "needs_review"
  | "no_3d"
  | "cad_pending"
  | "drawing_match"
  | "extrapolated"
  | "excluded"
  | "kind_overridden"
  | "no_drawing"
  | "few_samples";

/**
 * 每种提醒归哪一级（总纲 §5.5 P7：评审时逐条看）。
 * - 缺必填、未建草稿：这一行算不出价 → 挡出价
 * - 需确认（分类/用量拿不准）、没有 3D（读图估）、补了 3D 价没按它算、图纸按近似名配上、
 *   标外推（没有本厂真实价支撑）：价算了，依据要人看 → 要核对
 * - 已排除、人工改过分类、无图纸、样本少：知道就行 → 提示
 */
export const PROJECT_LINE_BADGE_TIERS: Readonly<
  Record<ProjectLineBadgeKey, ProjectLineBadgeTier>
> = {
  missing_inputs: "block",
  no_draft: "block",
  needs_review: "review",
  no_3d: "review",
  cad_pending: "review",
  drawing_match: "review",
  extrapolated: "review",
  excluded: "note",
  kind_overridden: "note",
  no_drawing: "note",
  few_samples: "note",
};

/* ═══════════════════════════════════════════════════════════════════════
 * 追加段 · 阶段一第2批（G1 / E3 / X7 / X14 / 五类来源图例）
 * ═══════════════════════════════════════════════════════════════════════ */

/** 公差档的叫法只认 toleranceTier.ts（总纲 §5.1 G1）；词表转出，便于全状态表一起测。 */
export const TOLERANCE_TIERS: readonly ToleranceTier[] = TIER_OPTIONS.map(
  (o) => o.value,
);
export { toleranceTierLabel };

/** 制造性风险每一条的等级（后端 A / B / i）→ 界面写字，不写字母（总纲 §5.4 S6）。 */
export const DFM_SEVERITY_META = {
  A: { label: "需评审", tone: "danger" },
  B: { label: "建议改", tone: "warning" },
  i: { label: "提示", tone: "default" },
} as const satisfies Record<DfmSeverity, QuoteStateLabel>;
export function dfmSeverityLabel(severity: unknown): QuoteStateLabel {
  return typeof severity === "string" &&
    Object.hasOwn(DFM_SEVERITY_META, severity)
    ? DFM_SEVERITY_META[severity as DfmSeverity]
    : { label: "风险待确认", tone: "warning" };
}
/** 报价体检（should-cost）每一条的等级。 */
export type ShouldCostSeverity = "high" | "medium" | "low";
export const SHOULD_COST_SEVERITY_META = {
  high: { label: "风险高", tone: "danger" },
  medium: { label: "要核对", tone: "warning" },
  low: { label: "提示", tone: "default" },
} as const satisfies Record<ShouldCostSeverity, QuoteStateLabel>;
export function shouldCostSeverityLabel(severity: unknown): QuoteStateLabel {
  const key = typeof severity === "string" ? severity.toLowerCase() : "low";
  return Object.hasOwn(SHOULD_COST_SEVERITY_META, key)
    ? SHOULD_COST_SEVERITY_META[key as ShouldCostSeverity]
    : SHOULD_COST_SEVERITY_META.low;
}

/** 五类来源图例（总纲 §5.1 G1「来源标签只用五个，图例写全」）。短标签与 FACTOR_SOURCE_META 一一对应。 */
export const FACTOR_SOURCE_LEGEND: readonly {
  shortLabel: string;
  kind: FactorSourceMeta["kind"];
  text: string;
}[] = [
  { shortLabel: "AI", kind: "ai", text: "AI 读图或估出来的" },
  {
    shortLabel: "AI · 待核对",
    kind: "ai",
    text: "AI 拿不准，或从图纸、需求单读来的，请核对",
  },
  { shortLabel: "人改", kind: "human", text: "有人改过，下面写着改之前的值" },
  { shortLabel: "目录", kind: "reference", text: "取自报价目录" },
  { shortLabel: "计算", kind: "reference", text: "按尺寸、重量等算出来的" },
];
export const FACTOR_SOURCE_LEGEND_NOTE =
  "只有小图标的：系统默认、本厂价、工厂政策等，点开看细分来源";

/** 字段写成问题（总纲 §5.7 E3）；只是可见文字，字段、取值、请求体不变。 */
export const QUOTE_FIELD_QUESTIONS = {
  qty: "要做几件？",
  mode: "材料谁出？",
  turnkey: "我们买料（包工包料）",
  laborOnly: "客户供料（来料加工）",
  customer: "客户是谁？（可以先不填）",
  machineRate: "用哪台机床的机时费？（可以不选）",
  tolerance: "精度要求",
} as const;

/** 来料方式的短词：向导选项「我们买料（包工包料）」「客户供料（来料加工）」括号里那个词，卡片、价格详情等短位置用。 */
export const PROCESSING_MODE_LABELS: Readonly<Record<string, string>> = {
  turnkey: "包工包料",
  labor_only: "来料加工",
};

/** 术语说明（总纲 §5.7 E3、§5.10 X14）：一句话说清对报价意味着什么。 */
export type QuoteTerm =
  | "机时费"
  | "外推"
  | "摊薄阈值"
  | "一次性投入"
  | "价格区间";
/** 「价格区间」说明的两句：区间那句只在真有 AI 估的工时上下限时成立（见 priceRangeHelpText）。 */
export const PRICE_RANGE_BAND_HELP =
  "按 AI 估的工时上限报价，现价就是区间的上沿。";
export const PROFIT_MARKUP_HELP = "利润率是在成本上加价，不是占售价的比例。";
export const QUOTE_TERM_HELP: Record<QuoteTerm, string> = {
  机时费:
    "机床开一小时收多少钱。加工费 = 工时 × 机时费；本厂报价政策写了管理费、刀具含在里面的，这两项不再另收。",
  外推: "这个数没有本厂真实成交价撑着，是按规则推出来的，可能偏差较大，发给客户前请核对。",
  摊薄阈值:
    "件数不超过它时，编程、装夹、调机这些一次性工时每件都按全额收；超过以后整单最多收这么多份，摊到每一件。",
  一次性投入:
    "为这个零件专门做的模具、夹具、检具。可以摊进单价，也可以整单单列收一次。",
  价格区间: PRICE_RANGE_BAND_HELP + PROFIT_MARKUP_HELP,
};

/** 按钮不用空词（总纲 §5.10 X7）：返回不合格的原因；合格 → null。 */
const EMPTY_ACTION_WORDS = ["确定", "好了", "没问题", "OK", "好的", "知道了"];
export function quoteActionLabelProblem(label: string): string | null {
  const text = label.replace(/\s+/g, "").trim();
  if (!text) return "按钮没有文字";
  if (EMPTY_ACTION_WORDS.some((w) => text.toUpperCase() === w.toUpperCase()))
    return `「${text}」是空词，写清点了会怎样`;
  if (text.startsWith("确定")) return `「${text}」以「确定」开头，改成动作`;
  return null;
}
